import { JsonValue } from '@bufbuild/protobuf';
import { getCoderByCoinName } from '@ensdomains/address-encoder';
import { parse } from '@marcbachmann/cel-js';
import {
  IPValidator,
  TldDomainValidator,
  UrlValidator,
} from '@q6cyber/js-validators';

import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import {
  decimalLimitValidation,
  isUrlCommonValidation,
  phoneValidation,
} from './common.validations';
import {
  DOB_FORMAT_PATTERN,
  EXPIRATION_FORMAT_PATTERN,
  IPV4_FORMAT_PATTERN,
} from './regex.validations';

const testListHelper = (
  val: JsonValue,
  testFn: (val: JsonValue) => ValidationErrors | null
): ValidationErrors | null => {
  let testResult = null;
  if (Array.isArray(val)) {
    val.forEach(e => {
      const currentTest = testFn(e);
      if (currentTest && Object.keys(currentTest).length) {
        testResult = currentTest;
      }
    });
  } else {
    testResult = testFn(val);
  }
  return testResult;
};

export const isURLValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const urlTest = (val: JsonValue) => {
    const value = (val as string) ?? '';
    return isUrlCommonValidation(value) || value === ''
      ? null
      : { invalidUrl: true };
  };
  return testListHelper(control.value, urlTest);
};

export const isBannerUrlValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  if (!control.value) return null;
  const isUrl = isURLValidator(control);
  if (isUrl && Object.keys(isUrl).length > 0) {
    const regex = /^\/[A-Z0-9\-_=/?&%#+.@:!$'()*,;~]+$/i;
    return regex.test(
      (control.value as string).replace(/https?:\/\/localhost(:\d+)?/gi, '')
    )
      ? null
      : isUrl;
  }
  return isUrl;
};

export function isIntegerValidator(
  control: AbstractControl
): ValidationErrors | null {
  const integerTest = (val: JsonValue) => {
    const value = val as number;
    if (value && !Number.isInteger(value)) {
      return { notInteger: true };
    }
    return null;
  };
  return testListHelper(control.value, integerTest);
}

/** The numeric proto types a form field can be backed by. */
export type ProtoNumberKind =
  'int32' | 'uint32' | 'int64' | 'uint64' | 'double';

/** What one of those fields holds: a 64-bit value arrives, and saves, as a string. */
export type LongNumberValue = number | string | null;

/** The 64-bit bounds are the safe-integer range, not the proto's: past it a number is no
 * longer the one that was typed, and the proto refuses what it is handed. */
export const PROTO_NUMBER_LIMITS: Record<
  ProtoNumberKind,
  { min: number; max: number; whole: boolean }
> = {
  int32: { min: -2_147_483_648, max: 2_147_483_647, whole: true },
  uint32: { min: 0, max: 4_294_967_295, whole: true },
  int64: {
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    whole: true,
  },
  uint64: { min: 0, max: Number.MAX_SAFE_INTEGER, whole: true },
  double: { min: -Number.MAX_VALUE, max: Number.MAX_VALUE, whole: false },
};

/** Everything one proto number type takes, in the shape Angular reports it. A numeric string
 * counts: a 64-bit field arrives from the proto as one, and is valid on the way back. */
const BIG_LIMITS: Record<'int64' | 'uint64', { min: bigint; max: bigint }> = {
  int64: { min: -(2n ** 63n), max: 2n ** 63n - 1n },
  uint64: { min: 0n, max: 2n ** 64n - 1n },
};

const WHOLE_STRING = /^-?\d+$/;

const is64Bit = (kind: ProtoNumberKind): kind is 'int64' | 'uint64' =>
  kind === 'int64' || kind === 'uint64';

export const protoNumberValidator =
  (kind: ProtoNumberKind): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;

    if (raw === null || raw === undefined || raw === '') return null;

    if (typeof raw !== 'number' && typeof raw !== 'string') {
      return { notFinite: true };
    }

    // A 64-bit field arrives as a string and can hold more than a number carries exactly, so
    // it is judged as written rather than through `Number`.
    if (
      is64Bit(kind) &&
      typeof raw === 'string' &&
      WHOLE_STRING.test(raw.trim())
    ) {
      const value = BigInt(raw.trim());
      const { min, max } = BIG_LIMITS[kind];

      if (value < min) {
        return { valueTooSmall: { min: min.toString(), actual: raw } };
      }

      if (value > max) {
        return { valueTooLarge: { max: max.toString(), actual: raw } };
      }

      return null;
    }

    const value = Number(raw);
    const { min, max, whole } = PROTO_NUMBER_LIMITS[kind];

    if (!Number.isFinite(value)) return { notFinite: true };
    if (whole && !Number.isInteger(value)) return { notInteger: true };
    if (value < min) return { valueTooSmall: { min, actual: value } };
    if (value > max) return { valueTooLarge: { max, actual: value } };

    return null;
  };

/** What a `long` column holds. Several fields are a wider type in the proto than in the
 * index they land in, and the index is the one that refuses the save. */
export const STORED_LONG_LIMITS = { min: -(2n ** 63n), max: 2n ** 63n - 1n };

/** Bounds a value by the `long` it is stored as, decimals included: those are kept in the
 * document as written, but a magnitude past the column is rejected outright. */
export const storedLongValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const raw = control.value;

  if (raw === null || raw === undefined || raw === '') return null;

  const text = `${raw}`.trim();
  const value = Number(text);

  // Anything that is not a number at all is another validator's to report.
  if (!Number.isFinite(value)) return null;

  const stored = WHOLE_STRING.test(text)
    ? BigInt(text)
    : BigInt(Math.trunc(value));

  if (stored < STORED_LONG_LIMITS.min) {
    return {
      valueTooSmall: { min: STORED_LONG_LIMITS.min.toString(), actual: raw },
    };
  }

  if (stored > STORED_LONG_LIMITS.max) {
    return {
      valueTooLarge: { max: STORED_LONG_LIMITS.max.toString(), actual: raw },
    };
  }

  return null;
};

export const requiredFieldValidator = (control: AbstractControl) => {
  const value = `${control.value ?? ''}`;

  const isEmpty = !value || value.trim().length === 0;
  return isEmpty ? { required: true, whitespace: true } : null;
};

const controlHasValue = (control: AbstractControl | null): boolean => {
  if (!control) {
    return false;
  }

  // Check if it's a FormArray
  if (control instanceof FormArray) {
    if (control.controls.length === 0) {
      return false;
    }
    // At least one item in the array must have a value
    return control.controls.some((item: AbstractControl) =>
      controlHasValue(item)
    );
  }

  // Check if it's a FormGroup
  if (control instanceof FormGroup) {
    const controlKeys = Object.keys(control.controls);
    if (controlKeys.length === 0) {
      return false;
    }
    // At least one control in the group must have a value
    return controlKeys.some((key: string) =>
      controlHasValue(control.controls[key])
    );
  }

  // It's a FormControl - check if it has a non-empty value
  const value = control.value;
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
};

export const multipleInputValidator = (checkList: string[]) => {
  return (control: AbstractControl) => {
    let invalid = true;
    const firstControl = control?.get(checkList[0]) as FormControl;

    for (const data of checkList) {
      const childControl = control.get(data);
      if (controlHasValue(childControl)) {
        invalid = false;
        break;
      }
    }
    if (invalid) {
      firstControl?.setErrors({
        ...firstControl.errors,
        atLeastOneInputHasData: { fields: checkList },
      });
    } else if (firstControl?.errors?.['atLeastOneInputHasData']) {
      const { atLeastOneInputHasData: _, ...remainingErrors } =
        firstControl.errors;
      const hasRemainingErrors = Object.keys(remainingErrors).length > 0;
      firstControl.setErrors(hasRemainingErrors ? remainingErrors : null);
    }
    return null;
  };
};

export const anyInputValidator = (banList: string[] = []) => {
  return (control: AbstractControl) => {
    const values = control.value;

    const hasValue = objectHasValue(values, banList);

    const firstKey = Object.keys(values)[0];
    if (hasValue) {
      control?.get(firstKey)?.setErrors(null);
    } else {
      control?.get(firstKey)?.setErrors({ atLeastOneInputHasData: true });
    }
    return null;
  };
};

const objectHasValue = (values: any, banList: string[]): boolean => {
  let hasValue = false;
  for (const key in values) {
    if (!banList.includes(key)) {
      if (!Array.isArray(values[key]) && typeof values[key] === 'object') {
        hasValue = objectHasValue(values[key], banList);
      } else if (values[key] && values[key] !== '0') {
        hasValue = true;
      }
    }
    if (hasValue) {
      break;
    }
  }
  return hasValue;
};

export const decimalValidator = (decimalLimit: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const decimalTest = (val: JsonValue) => {
      const formattedVal = val ?? 0;
      return decimalLimitValidation(formattedVal as number, decimalLimit)
        ? null
        : { tooManyDecimals: { limit: decimalLimit } };
    };
    return testListHelper(control.value, decimalTest);
  };
};

export const phoneValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const isPhoneTest = (val: JsonValue) => {
    return !val || phoneValidation(val as string) ? {} : { invalidPhone: true };
  };
  return testListHelper(control.value, isPhoneTest);
};

export const enumDefaultValidator = (
  enumObj: Record<string, any>
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    if (enumObj[control.value] === 0) {
      return { enumDefault: true };
    }
    return null;
  };
};

// Slack address or email validator
export const slackAddressValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (!value) {
    return null;
  }
  const emailControl = new FormControl(value);
  const emailError = Validators.email(emailControl);
  return value.startsWith('#') || !emailError
    ? null
    : { invalidSlackAddress: true };
};

export const isTLDDomainTemplate = (
  control: AbstractControl
): ValidationErrors | null => {
  const domainTest = (val: JsonValue) => {
    const value = (val ?? '')?.toString();
    if (!value) {
      return null;
    }
    return UrlValidator.isValidTorUrl(value) ||
      (UrlValidator.isValidUtf8Domain(value) &&
        TldDomainValidator.isValidTLDDomain(value)) ||
      TldDomainValidator.isValidTLDDomain(value) ||
      IPValidator.isValidIP(value)
      ? null
      : {
          invalidDomain: true,
        };
  };
  return testListHelper(control.value, domainTest);
};

export const disallowedCharactersValidator = (
  disallowedCharacters: string[]
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const disallowedTest = (val: JsonValue) => {
      const value = (val ?? '')?.toString();
      if (!value) {
        return null;
      }
      const found = disallowedCharacters.filter(character =>
        value.includes(character)
      );
      return found.length ? { disallowedCharacters: found } : null;
    };
    return testListHelper(control.value, disallowedTest);
  };
};

export const cardExpirationValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;
  if (!EXPIRATION_FORMAT_PATTERN.test(value))
    return { invalidExpiration: true };
  const month = parseInt(value.substring(0, 2), 10);
  return month >= 1 && month <= 12 ? null : { invalidExpiration: true };
};

export const dobValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = (control.value ?? '').toString().trim();
  if (!value) return null;
  if (!DOB_FORMAT_PATTERN.test(value)) return { invalidDob: true };

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  const isRealDate =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  if (!isRealDate) return { invalidDob: true };

  const now = new Date();
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  );
  if (date.getTime() > today) return { invalidDobFuture: true };

  return null;
};

export const ipListValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;
  const ips = value
    .split(',')
    .map((ip: string) => ip.trim())
    .filter(Boolean);
  const allValid = ips.every((ip: string) => {
    if (!IPV4_FORMAT_PATTERN.test(ip)) return false;
    return ip
      .split('.')
      .map(Number)
      .every((octet: number) => octet >= 0 && octet <= 255);
  });
  return allValid ? null : { invalidIpList: true };
};

/**
 * Checks the expression is syntactically valid CEL. Variables, functions and
 * the resulting type are not validated: the backend CEL environment declares
 * its own and stays the authority on them.
 */
export const celExpressionValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = (control.value ?? '').toString().trim();
  if (!value) return null;

  try {
    parse(value);
    return null;
  } catch (error) {
    return {
      invalidCelExpression: {
        message: `Invalid CEL syntax: ${(error as Error).message.split('\n')[0]}`,
      },
    };
  }
};

export const priceValidator = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  if (!value && value !== '0' && value !== 0) return null;
  const num = Number(value);
  return !isNaN(num) && num >= 0 ? null : { invalidPrice: true };
};

/** Currencies `@ensdomains/address-encoder` can check, by proto enum name. */
export const CRYPTO_ADDRESS_CODES: Record<string, string> = {
  ETHEREUM: 'eth',
  BITCOIN: 'btc',
  LITECOIN: 'ltc',
  DASH: 'dash',
  MONERO: 'xmr',
  DOGECOIN: 'doge',
  BITCOIN_CASH: 'bch',
};

const isValidCryptoAddress = (address: string, code: string): boolean => {
  try {
    return getCoderByCoinName(code).decode(address).length > 0;
  } catch {
    return false;
  }
};

export const cryptoAddressValidator = (
  currencyKey = 'cryptoCurrency',
  addressKey = 'walletId'
): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const currency = group.get(currencyKey)?.value;
    const address = group.get(addressKey)?.value;

    if (!currency && !address) {
      return { emptyWallet: true };
    }

    const code = CRYPTO_ADDRESS_CODES[currency];
    if (!code || !address) {
      return null;
    }

    return isValidCryptoAddress(address, code)
      ? null
      : { invalidWalletAddress: { currency: currency.replaceAll('_', ' ') } };
  };
};
